import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { contrastingColor, getNameOrInitials } from '../../../../utils/utils';
import { GreenroomState as GS } from '../../../../stores/types/types';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import { setModalState } from '../../../../stores/actions/modal';
import { MODAL_AVATAR_ACTIONS, MODAL_SAFETY_PHOTO_ACTIONS, MODAL_TEMPLATES } from '../../../../stores/reducers/modal';
import Avatar from '../../../general-components/avatar';
import { useMediaDevicesContext } from '../../../../contexts/media/check-media';
import { ConnectedUser } from '../../../../connection/socket';
import { GreenroomUser } from '../../../../stores/reducers/session';
import { ProfileInterface } from '../../../../stores/types/user-profiles';
import InfoBadge, { InfoBadgeMsg } from '../../../general-components/tooltips/info-badge';
import colors from '../../../../utils/colors';

interface Props {
	displayedProfile?: ConnectedUser | GreenroomUser | ProfileInterface | null;
}

const GradientDiv = styled.div`
	background: linear-gradient(180deg, rgba(24, 19, 20, 0.40) 0%, rgba(0, 0, 0, 0.00) 25%);
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
`;

export default function ProfileImages(props: Props) {
	const {displayedProfile} = props;
	const localUserId = useSelector((s: GS) => s.SessionReducer.user?.gr_user);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const host = useSelector((s: GS) => s.SessionReducer.host);

	const dispatch = useDispatch();

	const { checkMediaDevices } = useMediaDevicesContext();

	function openAvatarPhotoModal(isAvatar: boolean) {
		const template = isAvatar ? MODAL_TEMPLATES.AVATAR_PHOTO : MODAL_TEMPLATES.SAFETY_PHOTO;
		const action = isAvatar ? MODAL_AVATAR_ACTIONS.AVATAR : MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO;
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: template,
			modalData: {
				isOnboarding: false,
				modalAvatarAction: action,
				displayedProfile
			},
		}));
		checkMediaDevices();
	}

	const uploadButton = (isAvatar: boolean, style: React.CSSProperties) => (
		<button
			className='upload-button-profile'
			style={style}
			onClick={() => {
				openAvatarPhotoModal(isAvatar);
			}}>
			<Icon name={ICONS.UPLOAD} color={COLORS.TEXT_GRAY_200} size={12}></Icon>
		</button>
	);

	const background = useMemo(() => {
		if (!displayedProfile) return '';

		if ('bg_image' in displayedProfile) {
			return displayedProfile.bg_image;
		} 
		
		if ('poster' in displayedProfile) {
			return displayedProfile.poster;
		}

		if ('avatar' in displayedProfile) {
			return displayedProfile.avatar;
		}

		return '';
	}, [displayedProfile]);

	return (!displayedProfile
		? <></>
		: <><section className="settings-section" style={{ position: 'relative' }}>
			<div
				onClick={() => { openAvatarPhotoModal(false); }}
				className="bg-image-picker"
			>
				{background
					? <img
						src={background}
						alt={'Background'}
					/>
					: <div className="no-image-placeholder">
						<span style={{
							backgroundColor: meeting && meeting.primary_color ? `#${meeting.primary_color}` : '#333',
							color: contrastingColor(meeting && meeting.primary_color ? meeting.primary_color : colors.gray33),
						}}>
							{getNameOrInitials(displayedProfile, false)}
						</span>
					</div>
				}
				<GradientDiv />
				<div className="safety-photo-info">
					<label>Safety Photo</label>
					<InfoBadge
						message={InfoBadgeMsg.SafetyPhoto}
						customElement={<Icon name={ICONS.INFO_CIRCLE} color={COLORS.WHITE} size={12}></Icon>}
					/>
				</div>
				{uploadButton(false, {
					position: 'absolute',
					top: '16px',
					right: '16px',
				})}
			</div>
			<div
				className='avatar-image-picker'
				onClick={() => { openAvatarPhotoModal(true); }}
			>
				<Avatar
					user={displayedProfile}
					style={{
						width: '96px',
						height: '96px',
						cursor: 'pointer',
					}}
				/>
				{uploadButton(true, {
					right: '0',
					bottom: '0',
					position: 'absolute',
				})}
			</div>

		</section>
		{(displayedProfile.gr_user !== localUserId) 
			? <div style={{margin: '32px auto 48px auto'}}/>
			: <div style={{
				color: colors.blGrayscale300,
				margin: '32px auto 16px auto',
				width: 'fit-content',
				border: `1px solid ${colors.blGrayscale700}`,
				borderRadius: '88px',
				padding: '8px 16px',
				fontSize: '13px',
			}}>
				{host ? 'Producer' : 'Presenter'}
			</div>
		}
		</>
	);
}
